import type { Column } from '../../claim-history/simple-table'
import type { UseQueryResult } from '@tanstack/react-query'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { useTableSort } from '../../claim-history/simple-table/use-sort-hook'
import * as Card from '../../shared/card'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import { SearchInput } from '../components/search-input'

interface RequestProps {
  page: number
  count: number
  dir: string
  on: keyof LucentRxRequests
  q: string
}

interface LucentRxRequests extends Record<string, unknown>{
  id: string
  patient_issues_id: string
  patient_id: string
  encounter_id: string
  med_display: string
  med_req_notes: string
  provider_name: string
  provider_address: string
  provider_phone: string
  provider_fax: string
  provider_email: string
  npi_link: string
  uba_sent: boolean
  uba_sent_date: string
  uba_script_received: boolean
  uba_script_received_date: string
  document_id: string
  created_at: string
  updated_at: string
  patient_last_name: string
  patient_first_name: string
  patient_dob: string
  client_name: string
}

interface QueryData {
  data: LucentRxRequests[]
  total: number
  count: number
}

function useLucentRxScripts ({ page, count, dir, on, q }: RequestProps): UseQueryResult<QueryData, unknown> {
  return useQuery<QueryData, unknown, QueryData, ['lucent-rx-scripts', { page: number, count: number, dir: string, on: keyof LucentRxRequests, q: string}]>({
    queryKey: [ 'lucent-rx-scripts', { page, count, dir, on, q }],
    queryFn: async () => {
      let sp = new URLSearchParams([
        [ 'count', count.toString() ],
        [ 'page', page.toString() ],
        [ 'dir', dir.toString() ],
        [ 'on', on.toString() ],
        [ 'query', q ]
      ])

      return await http.getRequest(`/admin/lucent-rx-scripts?${sp.toString()}`)
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })
}

export function LucentRxScripts (): JSX.Element {
  const { sort, setSort } = useTableSort<LucentRxRequests>({ field: 'created_at', direction: 'desc' })
  let [ paging, setPaging ] = useState<{ page: number, pageSize: number }>({ page: 0, pageSize: 10 })
  let [ search, setSearch ] = useState<string>('')
  let [ debouncedSearch ] = useDebounceValue(search, 400)
  const query = useLucentRxScripts({
    page: paging.page,
    count: paging.pageSize,
    on: sort.field,
    dir: sort.direction,
    q: debouncedSearch
  })

  let columns = useMemo<Array<Column<LucentRxRequests>>>(() => [
    {
      name: 'patient_name',
      header: 'Patient Name',
      getValue: (item) => {
        return (
          <Link to={`/patient/${item.client_name}/${item.patient_id}`}>{item.patient_first_name} {item.patient_last_name}</Link>
        )
      }
    },
    {
      name: 'med_display',
      header: 'Medication'
    },
    {
      name: 'uba_sent_date',
      isSortable: true,
      header: 'Sent',
      getValue: (item) => {
        return (
          <FriendlyDateTime
            short
            datetime={item.uba_sent_date}
          />
        )
      }
    },
    {
      name: 'uba_script_received_date',
      isSortable: true,
      header: 'Received',
      getValue: (item) => {
        return (
          <FriendlyDateTime
            short
            datetime={item.uba_script_received_date}
          />
        )
      }
    },
    {
      name: 'created_at',
      header: 'Created at',
      isSortable: true,
      getValue: (item) => {
        return (
          <FriendlyDateTime
            short
            datetime={item.created_at}
          />
        )
      }
    }
  ], [])

  return (
    <Card.Card>
      <Card.Header>
        <h2>Lucent RX Scripts</h2>
        <SearchInput
          value={search}
          placeholder='Search by name'
          onChange={event => {
            setSearch(event.target.value)
          }}
        />
      </Card.Header>
      <Table.Table
        data={query?.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No records found'
        sort={sort}
        columns={columns}
        pagination={{ page: paging.page, pageSize: paging.pageSize, totalFiltered: query.data?.total }}
        onSortChange={(field, direction) => setSort({ field, direction })}
        onPageChange={(page) => setPaging((prev) => ({ ...prev, page: page - 1 }))}
        onPageSizeChange={(size) => setPaging((prev) => ({ pageSize: size, page: 0 }))}
      />
    </Card.Card>
  )
}
